import Footer from './components/Footer';
import { Link, useNavigate } from 'react-router-dom';
import MobileNav from './components/MobileNav';
import { signOut } from 'firebase/auth';
import { auth } from './firebase/config';
import { useAuth } from './context/AuthContext';
import { clearLocalDrafts } from './utils/clearDrafts';
import { FaRobot, FaFilePdf, FaCloud, FaCheckCircle, FaUsers, FaShieldAlt, FaRocket, FaMagic, FaFileAlt, FaBullseye } from 'react-icons/fa';
import TemplatePreviewCarousel from './components/TemplatePreviewCarousel';

function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('guest');
      clearLocalDrafts();
      navigate('/');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  const features = [
    {
      icon: <FaBullseye className="text-4xl text-emerald-600" />,
      title: "ATS Match Analyzer",
      description: "Paste any job description and get a match score, the exact keywords you're missing, and a summary rewritten for that role."
    },
    {
      icon: <FaRobot className="text-4xl text-blue-600" />,
      title: "AI-Powered Generation",
      description: "Let AI craft professional resume content tailored to your experience and job requirements in seconds."
    },
    {
      icon: <FaFileAlt className="text-4xl text-purple-600" />,
      title: "6 Professional Templates",
      description: "Choose from professionally designed, ATS-friendly templates that stand out to recruiters."
    },
    {
      icon: <FaFilePdf className="text-4xl text-red-600" />,
      title: "One-Click PDF Export",
      description: "Download a pixel-perfect PDF that matches your chosen template exactly. Ready for any application."
    },
    {
      icon: <FaCloud className="text-4xl text-green-600" />,
      title: "Cloud Storage",
      description: "Access and edit your resume from any device. Your progress is automatically saved."
    },
    {
      icon: <FaMagic className="text-4xl text-yellow-600" />,
      title: "Smart Start Wizard",
      description: "Guided step-by-step process to build your perfect resume with AI assistance."
    },
    {
      icon: <FaRocket className="text-4xl text-indigo-600" />,
      title: "Instant Preview",
      description: "See your resume come to life in real-time as you type. No surprises, just perfection."
    }
  ];

  const benefits = [
    "Save hours of formatting and design work",
    "Get past ATS (Applicant Tracking Systems)",
    "Professional quality without hiring a designer",
    "Update your resume in minutes, not days",
    "AI suggestions to improve your content",
    "Works on mobile, tablet, and desktop"
  ];



  const faqs = [
    {
      question: "Is there a free trial?",
      answer: "Yes! All users get free access with all 6 templates and unlimited PDF exports. Try 2 AI resume generations and 2 AI cover letters to see how Resume-Hero works for you."
    },
    {
      question: "How much does Premium cost?",
      answer: "Premium costs just $3/month. You get unlimited AI resume generation, unlimited cover letters, unlimited templates, and priority support."
    },
    {
      question: "Can I export my resume?",
      answer: "Absolutely! Export your resume as a polished PDF with one click. Every template is optimized for both ATS systems and human recruiters."
    },
    {
      question: "What is the ATS Match Analyzer?",
      answer: "Most companies screen resumes with software before a human sees them. Paste the job description and we score how well your resume matches it, list the important keywords you're missing, and offer a summary rewritten for that specific role. Free accounts get a few analyses each month; Premium is unlimited."
    },
    {
      question: "Will my data be safe?",
      answer: "Yes! We use Firebase (Google Cloud) for secure storage. Your data is encrypted and only accessible by you. We never share your information."
    },
    {
      question: "Can I cancel anytime?",
      answer: "Yes, you can cancel your subscription anytime from your dashboard. No hidden fees, no questions asked."
    },
    {
      question: "Do I need to create an account?",
      answer: "You can try the resume builder as a guest, but creating a free account lets you save your resume and access it from any device."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <MobileNav user={user} logout={handleLogout} />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 text-white py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Create a Standout Resume<br />in Minutes, Not Hours
          </h1>
          <p className="text-xl sm:text-2xl mb-4 max-w-3xl mx-auto text-blue-100">
            Score your resume against any job description, fix what's missing, and export a polished PDF.
          </p>
          <p className="text-lg sm:text-xl mb-8 max-w-2xl mx-auto text-blue-200">
            Join thousands of job seekers who've transformed their careers with Resume-Hero
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <Link
              to="/login"
              className="px-8 py-4 bg-white text-blue-700 font-bold rounded-lg shadow-xl hover:bg-gray-100 transition transform hover:scale-105 text-lg min-h-[56px] flex items-center justify-center"
            >
              Get Started Free
            </Link>
            <Link
              to="/select-template"
              className="px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-blue-700 transition text-lg min-h-[56px] flex items-center justify-center"
            >
              View Templates
            </Link>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-6 text-sm sm:text-base text-blue-100">
            <div className="flex items-center gap-2">
              <FaCheckCircle className="text-green-300" />
              <span>Free plan — no card needed</span>
            </div>
            <div className="flex items-center gap-2">
              <FaUsers className="text-blue-200" />
              <span>ATS-friendly templates</span>
            </div>
            <div className="flex items-center gap-2">
              <FaShieldAlt className="text-blue-200" />
              <span>Secure & encrypted</span>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof - Stats */}
      <section className="py-12 bg-gray-50 border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-blue-600 mb-2">6</div>
              <div className="text-gray-600 text-sm sm:text-base">ATS-Friendly Templates</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-purple-600 mb-2">Free</div>
              <div className="text-gray-600 text-sm sm:text-base">Unlimited PDF Exports</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-green-600 mb-2">5-day</div>
              <div className="text-gray-600 text-sm sm:text-base">Premium Free Trial</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-indigo-600 mb-2">$3/mo</div>
              <div className="text-gray-600 text-sm sm:text-base">Premium Price</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Create the Perfect Resume
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Powerful features designed to help you land your dream job
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition border border-gray-100"
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 sm:py-20 bg-blue-50 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Why Choose Resume-Hero?
              </h2>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <FaCheckCircle className="text-green-600 text-xl mt-1 flex-shrink-0" />
                    <span className="text-lg text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/login"
                className="inline-block mt-8 px-8 py-4 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition text-lg min-h-[56px] flex items-center justify-center"
              >
                Start Building Your Resume
              </Link>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Premium Features</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-700">AI Resume Generation</span>
                  <span className="font-bold text-green-600">Unlimited</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-700">AI Cover Letters</span>
                  <span className="font-bold text-green-600">Unlimited</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-700">Templates</span>
                  <span className="font-bold text-green-600">All 6 Free</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-700">Cloud Storage</span>
                  <span className="font-bold text-green-600">Unlimited</span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-gray-700">Priority Support</span>
                  <span className="font-bold text-green-600">24/7</span>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="text-center">
                  <div className="text-4xl font-bold text-gray-900 mb-2">$3<span className="text-lg text-gray-600">/month</span></div>
                  <p className="text-gray-600 mb-4">Cancel anytime</p>
                  <Link
                    to="/login"
                    className="block w-full px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition text-center"
                  >
                    Upgrade to Premium
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Template Preview Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Choose Your Perfect Template
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professionally designed templates that pass ATS systems and impress recruiters. See them in action below!
            </p>
          </div>
          <TemplatePreviewCarousel />
          <div className="text-center mt-8">
            <Link
              to="/select-template"
              className="inline-block px-8 py-4 bg-purple-600 text-white font-bold rounded-lg hover:bg-purple-700 transition text-lg min-h-[56px] flex items-center justify-center mx-auto"
            >
              Start Building with Templates
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works — replaces the previous testimonial carousel, which
          contained invented reviews from people who were not real customers. */}
      <section className="py-16 sm:py-20 bg-gray-50 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How Resume-Hero Works
            </h2>
            <p className="text-lg text-gray-600">
              From blank page to a tailored, ATS-ready resume in three steps
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                step: '1',
                title: 'Start with your details',
                body: 'Fill in the guided form, or let the Smart Start wizard draft your first version with AI.',
                color: 'from-blue-600 to-indigo-600'
              },
              {
                step: '2',
                title: 'Target the job',
                body: 'Paste a job description into the ATS Match Analyzer. Get a match score and the exact keywords you are missing.',
                color: 'from-emerald-600 to-teal-600'
              },
              {
                step: '3',
                title: 'Pick a template & export',
                body: 'Choose from 6 ATS-friendly designs and download a pixel-perfect PDF. Unlimited exports, free.',
                color: 'from-purple-600 to-pink-600'
              }
            ].map(({ step, title, body, color }) => (
              <div key={step} className="bg-white rounded-xl shadow-lg p-6 flex flex-col">
                <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${color} text-white font-bold text-xl flex items-center justify-center mb-4`}>
                  {step}
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">{title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about Resume-Hero
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden"
              >
                <summary className="px-6 py-4 font-bold text-gray-900 cursor-pointer hover:bg-gray-50 transition list-none flex items-center justify-between">
                  <span>{faq.question}</span>
                  <span className="text-blue-600 text-xl">+</span>
                </summary>
                <div className="px-6 py-4 text-gray-700 border-t border-gray-200 leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
            Ready to Land Your Dream Job?
          </h2>
          <p className="text-xl sm:text-2xl mb-8 text-blue-100">
            Join thousands of successful job seekers who use Resume-Hero
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login"
              className="px-8 py-4 bg-white text-blue-700 font-bold rounded-lg shadow-xl hover:bg-gray-100 transition transform hover:scale-105 text-lg min-h-[56px] flex items-center justify-center"
            >
              Get Started Free
            </Link>
            <Link
              to="/pricing"
              className="px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-blue-700 transition text-lg min-h-[56px] flex items-center justify-center"
            >
              View Pricing
            </Link>
          </div>
          <p className="mt-6 text-blue-200 text-sm sm:text-base">
            5-day free trial • Cancel anytime
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default HomePage;